import React, { useMemo } from 'react'
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query'
import { apiGet } from '~/util/api'
import { Button } from '~/lib/ui/button'
import ReminderDialog from './ReminderDialog'
import IncompleteReminder from './IncompleteReminder'
import { PlusIcon } from '@heroicons/react/24/outline'
import {
  PaginationControl,
  PaginationProvider,
  FilterSelect,
  usePagination,
} from './pagination'
import { TabNav, ButtonContainer, TabButton } from '~/components/shared/TabNav'
import { useIsMobile } from '~/hooks/useMediaQuery'
import useTranslation, {
  TranslationProvider,
} from '../shared/TranslationProvider'
import translations from './locales.json'

export const queryClient = new QueryClient()

const Reminders = ({ ...props }) => {
  const { locale } = props

  return (
    <QueryClientProvider client={queryClient}>
      <PaginationProvider>
        <TranslationProvider
          locale={locale}
          translations={translations}
          scope="javascript.components.Reminders"
        >
          <RemindersInner {...props} />
        </TranslationProvider>
      </PaginationProvider>
    </QueryClientProvider>
  )
}

const RemindersInner = () => {
  const { currentPage, perPage, filter, setPagination } = usePagination()
  const isMobile = useIsMobile()

  const path = useMemo(
    () =>
      `${window.location.pathname}.json?page=${currentPage}&per_page=${perPage}&filter=${filter}`,
    [currentPage, perPage, filter],
  )

  const queryFn = async () => {
    try {
      const res = await apiGet(path)
      setPagination(res.data.pagination)
      return res.data
    } catch (error) {
      console.error('Error fetching data:', error)
      throw new Error('Error fetching data')
    }
  }

  const { data } = useQuery({
    queryKey: ['reminders', path],
    queryFn,
  })

  const { reminders } = data || []

  const onChangeActiveTab = (activeTab) => {
    if (activeTab === 'messages') {
      window.location.href = '/coach_messages'
    }
  }

  const { t } = useTranslation('index')

  return (
    <div className="m-4 flex flex-col md:mx-9">
      <div className="mt-4">
        <h1 className="mb-0 !text-cloud-burst">
          <span className="font-normal">{t('view_your')}</span>
          {t('reminders')}
        </h1>
        <div className="mt-2 md:mb-4">{t('reminders_description')}</div>
        {isMobile && (
          <div className="mt-4">
            <ReminderDialog asChild>
              <Button variant="outline" className="max-md:w-full">
                <PlusIcon className="mr-2 h-6 w-6" />
                {t('new_reminder')}
              </Button>
            </ReminderDialog>
          </div>
        )}
        <TabNav
          defaultActiveTab="reminders"
          mobileMode="swipe"
          className="mb-4 md:flex md:flex-row md:justify-between md:border-b md:border-solid md:border-[#979797]"
          onChangeActiveTab={onChangeActiveTab}
        >
          <ButtonContainer
            className="justify-start md:border-none"
            carouselWrapperClass="min-h-[4.5rem]"
          >
            <TabButton
              name="messages"
              text={t('messages')}
              activeDecorationClass="bg-intoo-green-coachcta"
            />
            <TabButton name="reminders" text={t('reminders')} />
          </ButtonContainer>
          {!isMobile && (
            <ReminderDialog asChild>
              <Button variant="outline">
                <PlusIcon className="mr-2 h-6 w-6" />
                {t('new_reminder')}
              </Button>
            </ReminderDialog>
          )}
        </TabNav>
      </div>
      <div className="rounded-2.5xl border border-[#C0E2FF] bg-[#F7FCFF] px-4 py-7 md:px-8">
        <div className="flex flex-col">
          <h2 className="mb-4 text-primary">{t('reminders_panel_heading')}</h2>
          <div className="mb-6">{t('reminders_panel_description')}</div>
          <div className="mb-8 max-w-[13rem]">
            <div className="mb-2 font-bold">{t('filter_by_due_date')}</div>
            <FilterSelect />
          </div>
        </div>
        <div className="flex flex-col gap-y-6">
          {reminders?.map(
            (reminder, _index) =>
              !reminder.completed && (
                <IncompleteReminder key={reminder.id} reminder={reminder} />
              ),
          )}
        </div>
        <PaginationControl />
      </div>
    </div>
  )
}
export default Reminders
